import { IconStarEmpty16 } from '@warp-ds/icons/react/star-empty-16';
import { IconStarEmpty24 } from '@warp-ds/icons/react/star-empty-24';
import { IconStarFull16 } from '@warp-ds/icons/react/star-full-16';
import { IconStarFull24 } from '@warp-ds/icons/react/star-full-24';
import { IconStarHalf16 } from '@warp-ds/icons/react/star-half-16';
import { IconStarHalf24 } from '@warp-ds/icons/react/star-half-24';
import { memo } from 'react';

import type { Offer } from '@/interfaces';

export type Variant = 'full' | 'half' | 'empty';

export type StarRatingProps = Pick<Offer, 'rating'> & {
    id: string;
    bigStars?: boolean;
    emptyStars?: boolean;
};

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));
const toHalfStep = (n: number) => Math.round(n * 2) / 2;

export const StarRating = memo(({ id, rating, bigStars = false, emptyStars = false }: StarRatingProps) => {
    const norm = toHalfStep(clamp(rating, 0, 5));
    const full = Math.floor(norm);
    const hasHalf = norm % 1 === 0.5;

    const variantAt = (i: number): Variant => {
        if (i < full) return 'full';
        if (i === full && hasHalf) return 'half';
        return 'empty';
    };

    const Icons = bigStars
        ? { full: IconStarFull24, half: IconStarHalf24, empty: IconStarEmpty24 }
        : { full: IconStarFull16, half: IconStarHalf16, empty: IconStarEmpty16 };

    return (
        <div id={id} className="flex flex-row s-text-link gap-4" data-testid="starRatingContainer">
            <span className="sr-only">{norm} stjerners hotell</span>
            {Array.from({ length: 5 }, (_, i) => {
                const Star = Icons[variantAt(i)];

                return (
                    <Star
                        key={`${id}-star-${i + 1}`}
                        data-testid="starRating"
                        aria-hidden="true"
                        className={(!emptyStars && variantAt(i)) === 'empty' ? 'hidden' : undefined}
                    />
                );
            })}
        </div>
    );
});
